import {useCallback} from 'react';

import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

export enum ContainerRelevantItemsFilterOption {
  ALL = 'All',
  ENTRIES = 'Entries',
  ENTITIES = 'Entities',
  INVENTORY = 'Inventory',
  PROCESSES = 'Task groups',
  WORKSHEET_STEPS = 'Worksheet steps',
}

export interface ContainerRelevantItemsFilteredTableState {
  searchQuery: string;
  filterOption: ContainerRelevantItemsFilterOption;
  pageOffset: number;
}

export const containerRelevantItemsFilteredTableState =
  makeUseGlobalClientState<ContainerRelevantItemsFilteredTableState>({
    searchQuery: '',
    filterOption: ContainerRelevantItemsFilterOption.ALL,
    pageOffset: 0,
  });

export type UpdateContainerRelevantItemsFilteredTableState = (
  patch: Partial<ContainerRelevantItemsFilteredTableState>
) => void;

function patchResetsPageOffset(patch: Partial<ContainerRelevantItemsFilteredTableState>): boolean {
  return Object.hasOwn(patch, 'filterOption') || Object.hasOwn(patch, 'searchQuery');
}

export function mergeContainerRelevantItemsFilteredTableStatePatch(
  state: ContainerRelevantItemsFilteredTableState,
  patch: Partial<ContainerRelevantItemsFilteredTableState>
): ContainerRelevantItemsFilteredTableState {
  const nextState = {...state, ...patch};
  if (patchResetsPageOffset(patch)) {
    nextState.pageOffset = 0;
  }
  return nextState;
}

export function useContainerRelevantItemsFilteredTableState(
  containerId: string
): [ContainerRelevantItemsFilteredTableState, UpdateContainerRelevantItemsFilteredTableState] {
  const [state, setState] = containerRelevantItemsFilteredTableState.useGlobalClientState(containerId);

  const updateFilteredTableState = useCallback(
    (patch: Partial<ContainerRelevantItemsFilteredTableState>) => {
      setState(mergeContainerRelevantItemsFilteredTableStatePatch(state, patch));
    },
    [state, setState]
  );

  return [state, updateFilteredTableState];
}
